import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bomService } from '../services/api';

const UNIDADES = { '1':'Kilos', '2':'Litros', '3':'Pieza', '4':'Caja', '5':'Bidón', '6':'Servicio', '7':'Receta', '15':'Recetas' };

const TIPOS = {
  'lotnumberedinventoryitem': 'Inv. c/lote',
  'lotnumberedassemblyitem':  'Ensamble c/lote',
  'inventoryitem':            'Inventario',
  'assemblyitem':             'Ensamble',
  'noninventoryitem':         'No inventariable',
  'serviceitem':              'Servicio',
  'kititem':                  'Kit',
};

const TIPO_COLOR = {
  'Inv. c/lote':      { bg:'#e8f5e9', color:'#2e7d32' },
  'Ensamble c/lote':  { bg:'#e8eaf6', color:'#3949ab' },
  'Inventario':       { bg:'#e0f7fa', color:'#00838f' },
  'Ensamble':         { bg:'#f3e5f5', color:'#6A2C75' },
  'No inventariable': { bg:'#fff3e0', color:'#e65100' },
  'Servicio':         { bg:'#fce4ec', color:'#c2185b' },
};

export default function DetalleBOM() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bom, setBom]             = useState(null);
  const [loading, setLoading]     = useState(true);
  const [editando, setEditando]   = useState({});
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje]     = useState(null);
  const [error, setError]         = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await bomService.getById(id);
        setBom(data.data);
      } catch { setError('No se pudo cargar el BOM'); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const handleCantidad = (linea, valor) => setEditando(prev => ({ ...prev, [linea]: valor }));

  const handleGuardar = async () => {
    const cambios = Object.entries(editando)
      .filter(([_, v]) => v !== '' && !isNaN(v))
      .map(([linea, cantidad]) => ({ linea: parseInt(linea), cantidad: parseFloat(cantidad) }));
    if (cambios.length === 0) return;
    setGuardando(true); setMensaje(null); setError(null);
    try {
      const { data } = await bomService.updateComponentes(id, cambios);
      setMensaje(data.message);
      setEditando({});
      const res = await bomService.getById(id);
      setBom(res.data.data);
    } catch { setError('Error al guardar cambios en NetSuite'); }
    finally { setGuardando(false); }
  };

  const hayCambios = Object.keys(editando).length > 0;

  if (loading) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:80, color:'#BBA4C0' }}>
      <div style={{ fontSize:36, marginBottom:12 }}>⏳</div>
      <div style={{ fontSize:14 }}>Cargando BOM...</div>
    </div>
  );
  if (error && !bom) return (
    <div style={{ padding:40, textAlign:'center', color:'#c62828' }}>
      <div style={{ fontSize:36, marginBottom:12 }}>❌</div>
      {error}
    </div>
  );

  const totalKg = bom.componentes
    ?.filter(c => UNIDADES[String(c.unidad_id)] === 'Kilos')
    .reduce((sum, c) => sum + parseFloat(c.cantidad || 0), 0);

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ marginBottom:20 }}>
        <span onClick={() => navigate('/')}
          style={{ color:'#6A2C75', cursor:'pointer', fontSize:13, fontWeight:700, display:'inline-flex', alignItems:'center', gap:6 }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          ← Lista de Materiales
        </span>
      </div>

      {/* Header card */}
      <div style={{ background:'#fff', borderRadius:14, marginBottom:20, border:'1.5px solid #e0d5e8', overflow:'hidden', boxShadow:'0 2px 12px rgba(106,44,117,0.07)' }}>
        {/* Banda superior degradada */}
        <div style={{ height:5, background:'linear-gradient(90deg, #6A2C75, #D6A644)' }} />
        <div style={{ padding:'20px 24px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div style={{ flex:1 }}>
              <h1 style={{ fontSize:22, fontWeight:800, color:'#2a1a2e', marginBottom:4, lineHeight:1.2 }}>
                {bom.nombre || bom.codigo}
              </h1>
              {bom.codigo && bom.codigo !== bom.nombre && (
                <div style={{ fontSize:12, color:'#BBA4C0', fontWeight:700, letterSpacing:1, marginBottom:4 }}>
                  {bom.codigo}
                </div>
              )}
              {bom.descripcion && bom.descripcion !== bom.nombre && (
                <div style={{ fontSize:13, color:'#7a6882', marginTop:4 }}>{bom.descripcion}</div>
              )}
            </div>
            <div style={{ textAlign:'right', marginLeft:20 }}>
              <span style={{ background:'#f5f0fa', color:'#6A2C75', borderRadius:20, padding:'5px 16px', fontSize:12, fontWeight:700, display:'block', marginBottom:8 }}>
                {bom.clase || 'Sin clase'}
              </span>
              <div style={{ fontSize:11, color:'#BBA4C0' }}>ID NetSuite: {bom.id_netsuite}</div>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display:'flex', gap:16, marginTop:16, flexWrap:'wrap' }}>
            <div style={{ background:'#f5f0fa', borderRadius:10, padding:'10px 16px', display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:18 }}>📋</span>
              <div>
                <div style={{ fontSize:18, fontWeight:800, color:'#6A2C75' }}>{bom.componentes?.length || 0}</div>
                <div style={{ fontSize:11, color:'#7a6882' }}>Componentes</div>
              </div>
            </div>
            {totalKg > 0 && (
              <div style={{ background:'#fff8e8', borderRadius:10, padding:'10px 16px', display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:18 }}>⚖️</span>
                <div>
                  <div style={{ fontSize:18, fontWeight:800, color:'#D6A644' }}>{totalKg.toFixed(4)}</div>
                  <div style={{ fontSize:11, color:'#7a6882' }}>Total kg</div>
                </div>
              </div>
            )}
            <div style={{ background:'#f0faf4', borderRadius:10, padding:'10px 16px', display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:18 }}>🕐</span>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:'#2e7d32' }}>
                  {new Date(bom.ultima_sync).toLocaleDateString('es-MX')}
                </div>
                <div style={{ fontSize:11, color:'#7a6882' }}>Última sync</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mensajes */}
      {mensaje && (
        <div style={{ background:'#e8f5e9', color:'#2e7d32', padding:'12px 16px', borderRadius:10, marginBottom:16, fontSize:13, display:'flex', alignItems:'center', gap:8, borderLeft:'4px solid #2e7d32' }}>
          ✅ {mensaje}
        </div>
      )}
      {error && (
        <div style={{ background:'#fdecea', color:'#c62828', padding:'12px 16px', borderRadius:10, marginBottom:16, fontSize:13, borderLeft:'4px solid #c62828' }}>
          ❌ {error}
        </div>
      )}

      {/* Tabla de componentes */}
      <div style={{ background:'#fff', borderRadius:14, border:'1.5px solid #e0d5e8', overflow:'hidden', boxShadow:'0 2px 12px rgba(106,44,117,0.07)' }}>
        <div style={{ padding:'16px 24px', borderBottom:'1px solid #e0d5e8', display:'flex', justifyContent:'space-between', alignItems:'center', background:'#faf8fc' }}>
          <h2 style={{ fontSize:15, fontWeight:800, color:'#6A2C75' }}>
            Componentes
            <span style={{ background:'#6A2C75', color:'#fff', borderRadius:20, padding:'2px 10px', fontSize:12, marginLeft:10, fontWeight:700 }}>
              {bom.componentes?.length || 0}
            </span>
          </h2>
          {hayCambios && (
            <button onClick={handleGuardar} disabled={guardando} style={{
              background: guardando ? '#BBA4C0' : 'linear-gradient(135deg, #D6A644, #e8c060)',
              color:'#fff', border:'none', borderRadius:8, padding:'9px 20px',
              cursor: guardando ? 'default' : 'pointer', fontSize:13, fontWeight:700,
              boxShadow: guardando ? 'none' : '0 2px 8px rgba(214,166,68,0.4)',
              display:'flex', alignItems:'center', gap:8,
            }}>
              {guardando ? '⏳ Guardando...' : `💾 Guardar ${Object.keys(editando).length} cambio(s)`}
            </button>
          )}
        </div>

        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#f5f3f0' }}>
                {['#', 'Componente', 'Tipo', 'Cantidad', 'Unidad', 'Fuente'].map(h => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#7a6882', borderBottom:'1px solid #e0d5e8', textTransform:'uppercase', letterSpacing:0.8, whiteSpace:'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(bom.componentes || []).map((c, i) => {
                const editado  = editando[c.linea] !== undefined;
                const tipoLabel = TIPOS[c.item_tipo] || c.item_tipo || '—';
                const tipoStyle = TIPO_COLOR[tipoLabel] || { bg:'#f5f3f0', color:'#7a6882' };
                return (
                  <tr key={c.id} style={{ background: editado ? '#fffbf0' : i % 2 === 0 ? '#fff' : '#fdfcff', transition:'background 0.15s' }}>
                    <td style={{ padding:'12px 16px', fontSize:13, color:'#D6A644', width:40, fontWeight:800 }}>{c.linea}</td>
                    <td style={{ padding:'12px 16px', minWidth:200 }}>
                      <div style={{ fontSize:14, fontWeight:600, color:'#2a1a2e', lineHeight:1.3 }}>{c.item_nombre}</div>
                      {c.item_id && (
                        <div style={{ fontSize:11, color:'#BBA4C0', marginTop:3, fontFamily:'monospace' }}>#{c.item_id}</div>
                      )}
                    </td>
                    <td style={{ padding:'12px 16px', whiteSpace:'nowrap' }}>
                      <span style={{ background:tipoStyle.bg, color:tipoStyle.color, borderRadius:6, padding:'3px 10px', fontSize:11, fontWeight:700 }}>
                        {tipoLabel}
                      </span>
                    </td>
                    <td style={{ padding:'8px 16px', width:140 }}>
                      <input
                        type="number" step="0.0001" min="0"
                        value={editado ? editando[c.linea] : c.cantidad}
                        onChange={e => handleCantidad(c.linea, e.target.value)}
                        style={{
                          width:'100%', padding:'7px 10px', borderRadius:8, fontSize:13,
                          border: editado ? '2px solid #D6A644' : '1.5px solid #e0d5e8',
                          outline:'none', background: editado ? '#fffbf0' : '#fff',
                          fontWeight: editado ? 700 : 400, transition:'all 0.15s',
                          boxShadow: editado ? '0 0 0 3px rgba(214,166,68,0.15)' : 'none',
                        }}
                      />
                    </td>
                    <td style={{ padding:'12px 16px', whiteSpace:'nowrap' }}>
                      <span style={{ background:'#f5f0fa', color:'#6A2C75', borderRadius:6, padding:'3px 10px', fontSize:12, fontWeight:700 }}>
                        {UNIDADES[String(c.unidad_id)] || `ID:${c.unidad_id}`}
                      </span>
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:12, color:'#7a6882' }}>{c.fuente}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {(!bom.componentes || bom.componentes.length === 0) && (
          <div style={{ padding:60, textAlign:'center', color:'#BBA4C0' }}>
            <div style={{ fontSize:32, marginBottom:12 }}>📋</div>
            Este ensamble no tiene componentes registrados
          </div>
        )}
      </div>
    </div>
  );
}